import * as React from 'react';
import { Key, ReactNode, useContext, useEffect, useState } from 'react';
import { ImageServiceApi, UsersDTO } from '../../api/admin';
import { Avatar } from 'primereact/avatar';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { PersonDTO } from '../../api/core';

interface Props {
  type: 'small' | 'large';
  user?: UsersDTO;
  person?: PersonDTO;
  key?: Key;
  currentUser?: boolean;
}

export const UserAvatarComponent = (props: Props): ReactNode => {
  const {actions, user, avatar} = useContext(AuthContext);
  const imageApi: ImageServiceApi = new ImageServiceApi().withMiddleware(getAuthMiddleware(actions));
  const [avatarUrl, setAvatarUrl] = useState<string>(undefined);
  const [name, setName] = useState<string>(undefined);
  const [initials, setInitials] = useState<string>(undefined);

  useEffect((): void => {
    if(props.currentUser) {
      setName(user ? `${user.givenname} ${user.surname}` : 'unknown');
      setInitials(user ? `${user.givenname[0] ?? ''}${user.surname[0] ?? ''}` : '??');
    }
  }, [props.currentUser, user]);

  useEffect((): void => {
    if(props.currentUser) {
      setAvatarUrl(avatar ? URL.createObjectURL(avatar) : null);
    }
  }, [avatar, props.currentUser]);

  const getPersonName = (): { given: string; surname: string } => {
    if (props.user) {
      return { given: props.user.givenname ?? '', surname: props.user.surname ?? '' };
    }
    const main = props.person?.personNames?.find(p => p.isMain);
    return { given: main?.givenname ?? '', surname: main?.surname ?? '' };
  };

  const updateName = (): void => {
    const { given, surname } = getPersonName();
    const fullName = `${given} ${surname}`.trim();
    setName(fullName || 'unnamed');
    setInitials(`${given[0] ?? ''}${surname[0] ?? ''}`.toUpperCase());
  };

  const updateAvatar = async (): Promise<void> => {
    let blob: Blob | null = null;
    try {
      if (props.user && props.user.id !== undefined) {
        blob = await imageApi.downloadFile({ userId: props.user.id });
      } else if (props.person && props.person.id !== undefined) {
        blob = await imageApi.findByPersonId({ personId: props.person.id });
      }
      if (blob) {
        setAvatarUrl(URL.createObjectURL(blob));
      } else {
        if(avatar !== null) setAvatarUrl(null);
      }
    } catch {
      if(avatar !== null) setAvatarUrl(null);
      console.warn('No avatar found.');
    }
  };

  useEffect((): void => {
    if (props.currentUser) return;
    updateName();
    void updateAvatar();
  }, [props.user, props.person, props.currentUser]);

  switch (props.type) {
    case 'large':
      return (
        <div
          className={
            'w-full h-full bg-primary border-circle overflow-hidden flex align-items-center justify-content-center'
          }
        >
          {avatarUrl ? (
            <div className={'w-full h-full bg-white'}>
              <img src={avatarUrl} alt={'Avatar'} style={{ objectFit: 'cover' }} className={'w-full h-full'} title={name}/>
            </div>
          ) : initials ? (
            <span title={name} style={{ fontSize: '6em' }}>
              {initials}
            </span>
          ) : (
            <i
              className="pi pi-user mt-3 p-3"
              style={{
                fontSize: '5em',
                borderRadius: '50%',
                backgroundColor: 'var(--surface-b)',
                color: 'var(--surface-d)',
              }}
            ></i>
          )}
        </div>
      );
    case 'small':
    default:
      return <Avatar shape="circle" image={avatarUrl || undefined} label={!avatarUrl ? initials : undefined} title={name}/>;
  }

};
